// forked from https://github.com/GetStream/stream-chat-react/blob/master/src/components/ChannelPreview/ChannelPreviewMessenger.tsx

import React, { useEffect, useRef, useState } from 'react'
import { useChatContext } from 'stream-chat-react'
import GroupOutlinedIcon from 'components/material-symbol/icons/Group'
import NotificationsOffOutlinedIcon from 'components/material-symbol/icons/NotificationsOff'
import CircularHighlightedIcon from 'components/circular-highlighted-icon'
import { makeStyles } from '@material-ui/styles'
import {
  formatChannelPreviewText,
  formatMessageListTime,
  getChannelLastMessageAt,
  getChannelLatestMessage,
  getContactRoleLabel,
} from './messaging-utils'
import MessagingChannelPreviewAttachment, {
  isAttachmentPreviewText,
} from './messaging-channel-preview-attachment'
import MessagingContactAvatar from './messaging-contact-avatar'

const useStyles = makeStyles(theme => ({
  previewButton: {
    width: '100%',
    maxWidth: '100%',
    overflow: 'visible',
    minHeight: 72,
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    alignItems: 'center',
    textAlign: 'left',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 0,
    '&:hover': {
      backgroundColor: 'rgba(0, 35, 75, 0.04)',
    },
  },
  activePreview: {
    backgroundColor: '#F6F6F6',
    boxShadow: 'inset 3px 0 0 #BFC8CC',
    '&:hover': {
      backgroundColor: '#F6F6F6',
    },
  },
  content: {
    overflow: 'hidden',
    minWidth: 0,
    flex: 1,
    marginLeft: theme.spacing(1.5),
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.25),
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
    flex: 1,
  },
  name: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
  },
  roleLabel: {
    flexShrink: 0,
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontWeight: 400,
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flexShrink: 0,
  },
  timestamp: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.blue.badge,
    flexShrink: 0,
  },
  lastMessage: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 400,
    fontSize: '0.8125rem',
    lineHeight: 1.25,
    color: theme.palette.text.secondary,
    margin: 0,
  },
}))

const isAttachmentPreviewElement = preview =>
  React.isValidElement(preview) &&
  preview.type === MessagingChannelPreviewAttachment

const extractPlainTextFromPreview = preview => {
  if (!preview) return ''
  if (typeof preview === 'string') return preview
  if (!React.isValidElement(preview)) return String(preview)

  const { children } = preview.props || {}

  if (typeof children === 'string' || typeof children === 'number') {
    return String(children)
  }

  if (Array.isArray(children)) {
    return children.map(child => extractPlainTextFromPreview(child)).join('')
  }

  if (React.isValidElement(children)) {
    return extractPlainTextFromPreview(children)
  }

  return ''
}

const formatPreviewText = (text, members) => {
  let messageTextToRender = text
  members.forEach(member => {
    const { id, name } = member.user ?? {}
    if (!id) return
    messageTextToRender = messageTextToRender.replaceAll(`@${id}`, `@${name}`)
  })
  return formatChannelPreviewText(messageTextToRender)
}

const UnMemoizedChannelPreviewMessenger = props => {
  const {
    active,
    channel,
    latestMessagePreview,
    setActiveChannel,
    unread,
    watchers,
  } = props
  const { client } = useChatContext()
  const classes = useStyles()

  const [isMuted, setIsMuted] = useState(() => channel.muteStatus().muted)

  useEffect(() => {
    const handler = () => setIsMuted(channel.muteStatus().muted)
    client?.on('notification.channel_mutes_updated', handler)
    return () => client?.off('notification.channel_mutes_updated', handler)
  }, [channel, client])

  const currentUserId = client.user?.id
  const { image, name } = channel?.data || {}
  const allMembers = Object.values(channel?.state.members || {})
  const otherMembers = allMembers
    .filter(m => m.user_id !== currentUserId)
    .map(m => m.user)

  const title = (() => {
    if (name) return name
    if (otherMembers.length > 2) {
      return `${otherMembers
        .slice(0, 2)
        .map(u => u.name)
        .join(', ')}, and ${otherMembers.length - 2} others`
    }
    return otherMembers.map(u => u.name).join(', ')
  })()

  const roleLabel =
    otherMembers.length === 1 ? getContactRoleLabel(otherMembers[0]) : null

  const avatar = (() => {
    if (isMuted) {
      return (
        <CircularHighlightedIcon
          icon={NotificationsOffOutlinedIcon}
          size={40}
          iconSize={24}
          iconColor="#FFFFFF"
          highlightColor="#00234B"
        />
      )
    }
    if (otherMembers.length > 1) {
      return (
        <CircularHighlightedIcon
          icon={GroupOutlinedIcon}
          size={40}
          iconSize={22}
          iconColor="#172024"
          highlightColor="#EBF0F2"
        />
      )
    }
    return <MessagingContactAvatar image={image} name={title} />
  })()

  const channelPreviewButton = useRef(null)

  const message = (() => {
    const preview = latestMessagePreview
    const latestMessage = getChannelLatestMessage(channel)
    const rawText = latestMessage?.text?.trim()

    if (rawText) {
      return formatPreviewText(rawText, allMembers)
    }

    if (!preview) return ''

    if (isAttachmentPreviewElement(preview)) {
      return preview
    }

    if (typeof preview === 'string') {
      if (isAttachmentPreviewText(preview)) {
        return <MessagingChannelPreviewAttachment />
      }
      return formatPreviewText(preview, allMembers)
    }

    if (React.isValidElement(preview) && preview.type === 'p') {
      const plainText = extractPlainTextFromPreview(preview)
      if (plainText) {
        return formatPreviewText(plainText, allMembers)
      }
    }

    return preview
  })()

  const onSelectChannel = () => {
    if (setActiveChannel) {
      setActiveChannel(channel, watchers)
    }
    if (channelPreviewButton?.current) {
      channelPreviewButton.current.blur()
    }
  }

  const hasUnread = (unread ?? 0) >= 1
  const lastMessageAt = getChannelLastMessageAt(channel)

  return (
    <button
      type="button"
      className={`str-chat__channel-preview-messenger ${
        hasUnread ? 'str-chat__channel-preview-messenger--unread' : ''
      } ${active ? 'str-chat__channel-preview-messenger--active' : ''} ${
        active ? classes.activePreview : ''
      } ${classes.previewButton}`}
      data-testid="channel-preview-button"
      onClick={onSelectChannel}
      ref={channelPreviewButton}
    >
      <div className="str-chat__channel-preview-messenger--left">{avatar}</div>
      <div className={`str-chat__channel-preview-end ${classes.content}`}>
        <div className={classes.topRow}>
          <div className={classes.nameRow}>
            <span
              className={`str-chat__channel-preview-messenger--name ${classes.name}`}
              title={title}
            >
              {title}
            </span>
            {roleLabel && (
              <span className={classes.roleLabel}>({roleLabel})</span>
            )}
          </div>
          <div className={classes.meta}>
            {lastMessageAt && (
              <span className={classes.timestamp}>
                {formatMessageListTime(lastMessageAt)}
              </span>
            )}
            {hasUnread && <span className={classes.unreadDot} aria-hidden />}
          </div>
        </div>
        <div
          className={`str-chat__channel-preview-messenger--last-message ${classes.lastMessage}`}
        >
          {message}
        </div>
      </div>
    </button>
  )
}

const ChannelPreviewMessenger = React.memo(UnMemoizedChannelPreviewMessenger)
export default ChannelPreviewMessenger
